import { useState, useRef, useEffect } from 'react'
import { Combobox } from '@headlessui/react'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import { FileText, Plus } from 'lucide-react'
import predefinedDocumentTypes from './PredefinedDocumentTypes'

export function DocTypeNameCombo({
    docTypeName,
    docTypeNameRef,
    setDocTypeName,
    setDocTypeDescription,
    setUniqueWords,
}) {
    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const comboboxRef = useRef(null)

    // filter list by query
    const filtered = predefinedDocumentTypes.filter((dt) =>
        dt.name.toLowerCase().includes(query.toLowerCase())
    )

    // check if the query matches any predefined type exactly
    const hasExactMatch = predefinedDocumentTypes.some(
        (dt) => dt.name.toLowerCase() === query.toLowerCase()
    )

    // click-outside handler to close the list
    useEffect(() => {
        const onClick = (e) => {
            if (comboboxRef.current && !comboboxRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', onClick)
        return () => document.removeEventListener('mousedown', onClick)
    }, [])

    // when you pick a real item, set all the bits and close
    const handleChange = (value) => {
        setDocTypeName(value)
        const picked = predefinedDocumentTypes.find((dt) => dt.name === value)
        if (picked) {
            setDocTypeDescription(picked.description)
            setUniqueWords(picked.uniqueWords)
        }
        setIsOpen(false)
    }

    return (
        <div ref={comboboxRef}>
            <Combobox
                value={docTypeName}
                onChange={handleChange}
                open={isOpen}
                onOpenChange={setIsOpen}
            >
                <div className="relative">
                    <Combobox.Input
                        className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        placeholder="e.g., invoice"
                        displayValue={() => docTypeName}
                        onFocus={() => setIsOpen(true)}
                        onChange={(e) => {
                            setQuery(e.target.value)
                            setDocTypeName(e.target.value)
                        }}
                        ref={docTypeNameRef}
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                    </Combobox.Button>

                    {isOpen && (
                        <Combobox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {filtered.map((dt) => (
                                <Combobox.Option
                                    key={dt.id}
                                    value={dt.name}
                                    className={({ active }) =>
                                        `cursor-default select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                    }
                                >
                                    {({ active, selected }) => (
                                        <>
                                            <div className="flex items-center">
                                                <FileText className="h-5 w-5 text-gray-400 mr-2" />
                                                <span className={active ? 'font-semibold' : 'font-normal'}>
                                                    {dt.name}
                                                </span>
                                            </div>
                                            {selected && (
                                                <span
                                                    className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'
                                                        }`}
                                                >
                                                    <CheckIcon className="h-5 w-5" />
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Combobox.Option>
                            ))}
                            {query && !hasExactMatch && (
                                <Combobox.Option
                                    value={query}
                                    className={({ active }) =>
                                        `cursor-default select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                    }
                                >
                                    {({ active }) => (
                                        <div className="flex items-center">
                                            <Plus className="h-5 w-5 text-gray-400 mr-2" />
                                            <span className={active ? 'font-semibold' : 'font-normal'}>
                                                Add new document type "{query}"
                                            </span>
                                        </div>
                                    )}
                                </Combobox.Option>
                            )}
                        </Combobox.Options>
                    )}
                </div>
            </Combobox>
        </div>
    )
}